import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { getApplicationsFromCache, saveApplicationsToCache } from '../lib/offlineStorage';
import { trackPixel, applicationEventId, reportJobView, reportApplyIntent, applyTrackingFields } from '../lib/pixel';
import config, { fixImageUrl } from '../config';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { MapPin, DollarSign, Briefcase, ArrowLeft, Send, MessageCircle, Star, Trash, Heart, Share2, Facebook, Shield, Edit, Trash2, Link2, ChevronDown, MessageSquare, Phone, Download, Upload, FileText, CheckCircle, Loader2, Users, Printer, ThumbsUp, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import ReviewsListModal from '../components/ReviewsListModal';
import ReportButton from '../components/ReportButton';
import PesoBadge from '../components/PesoBadge';
import { useSavedJobs } from '../hooks/useSavedJobs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import ApplicantDetailModal from '../components/ApplicantDetailModal';
import EditJobModal from '../components/EditJobModal';
import NextJobsPanel from '../components/NextJobsPanel';
import SEO, { generateJobPostingSchema, generateBreadcrumbSchema } from '../components/SEO';
import ImageLightbox from '../components/ImageLightbox';
import BannerCreateModal from '../components/BannerCreateModal';
import { isEmployer as isEmployerRole } from '../lib/roleHelpers';
import { usePricing } from '../hooks/usePricing';
import { rememberReturnTo, peekReturnTo, clearReturnTo } from '../lib/returnTo';
import { saveApplyDraft, loadApplyDraft, clearApplyDraft, findSavedMatch, isResumeDoc } from '../lib/applyDraft';
import { normalizePhonePH } from '../lib/phoneAuth';
import { splitJobDocuments } from '../lib/jobDocuments';

// Application statuses an employer can still answer (mirrors the backend's allowlist).
const DECIDABLE_STATUSES = ['submitted', 'applied', 'pending', 'revealed', 'shortlisted', 'rejected'];

const JobDetails = ({ profile, setProfile }) => {
  const { slug } = useParams();  // This can be either a slug or UUID
  const id = slug;  // Backend handles both formats
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const isCebuano = currentLang === 'ceb';
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check if this is a gig (stored in jobs table with post_type='gig')
  const isGig = job?.post_type === 'gig';
  
  // Helper to get localized field
  const getLocalizedField = (obj, field) => {
    if (!obj) return '';
    if (isCebuano && obj[`${field}_ceb`]) {
      return obj[`${field}_ceb`];
    }
    return obj[field] || '';
  };
  // Requirements to display: prefer Cebuano list, fall back to English when it's missing.
  // (Guarding on job.requirements alone but then reading requirements_ceb.length crashed the page.)
  const displayedRequirements = (isCebuano && job?.requirements_ceb) ? job.requirements_ceb : job?.requirements;
  const jobDocs = splitJobDocuments(job?.required_documents);
  const jobDocLines = (
    <>
      {jobDocs.toApply.length > 0 && (
        <li className="text-primary font-medium">
          📎 {t('jobDocs.neededToApplyLine', 'Needed to apply')}: {jobDocs.toApply.map(d => d.name).join(', ')}
        </li>
      )}
      {jobDocs.ifHired.length > 0 && (
        <li className="text-emerald-700 font-medium">
          📎 {t('jobDocs.ifHiredLine', 'Bring if hired (not needed to apply)')}: {jobDocs.ifHired.map(d => d.name).join(', ')}
        </li>
      )}
    </>
  );
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [showCBApplyDialog, setShowCBApplyDialog] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [cover, setCover] = useState('');
  const [cbCover, setCbCover] = useState('');
  const [cbApplying, setCbApplying] = useState(false);

  // Claim flow states (for employers claiming community board jobs)
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [claimStep, setClaimStep] = useState('request'); // 'request' | 'verify'
  const [claimCode, setClaimCode] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimEmail, setClaimEmail] = useState('');
  const [customAnswer, setCustomAnswer] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  // "Needed if hired" documents the seeker ticked "I'll bring this later" on (on-screen only).
  const [laterDocs, setLaterDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [applying, setApplying] = useState(false);
  // Apply dialog step: 'form' (cover letter) -> 'next' ("here are 3 more jobs that fit you").
  const [applyStep, setApplyStep] = useState('form');
  const [nextJobs, setNextJobs] = useState([]);
  const [nextJobsLoading, setNextJobsLoading] = useState(false);
  const [nextApplied, setNextApplied] = useState(() => new Set());
  const nextJobsRequestRef = useRef(0); // ignore a slow next-jobs response that belongs to an earlier apply
  const [myApplication, setMyApplication] = useState(null);
  const [applicationChecked, setApplicationChecked] = useState(false);
  // Files this seeker already uploaded for earlier applications (+ their profile resume), offered
  // again in the apply pop-up so they don't have to dig the same documents up for every job.
  const [savedDocs, setSavedDocs] = useState([]);
  const [savedResume, setSavedResume] = useState(null);
  const [savedDocsLoaded, setSavedDocsLoaded] = useState(false);
  const resumeAttachTriedRef = useRef(false);
  // Phone number typed inside the apply pop-up (for seekers whose profile has none yet)
  const [phoneInput, setPhoneInput] = useState('');
  
  // Gig bidding states
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [myBid, setMyBid] = useState(null);
  const [bidData, setBidData] = useState({ amount: '', timeline: '', description: '' });
  const [bidding, setBidding] = useState(false);
  
  // Employer applications state
  const [applications, setApplications] = useState([]);
  const [decidingId, setDecidingId] = useState(null);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showApplicantDetail, setShowApplicantDetail] = useState(false);
  
  // Save job functionality
  const isSeeker = profile?.role === 'job_seeker' || profile?.role === 'seeker';
  const { savedJobIds, toggleSaveJob } = useSavedJobs(profile);
  const { pricing } = usePricing();
  // Use the actual job.id (UUID) instead of the URL param which could be a slug
  const isSaved = job && savedJobIds.includes(job.id);
  
  // Image lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Edit/Delete functionality
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const isOwner = profile && job && job.employer_id === profile.id;
  const isAdmin = profile?.role === 'admin';
  const canEdit = isOwner || isAdmin;

  // Employer banner enticement: null = unknown/loading, true/false once checked.
  const [hasActiveBanner, setHasActiveBanner] = useState(null);
  const [showBannerCreateModal, setShowBannerCreateModal] = useState(false);

  // Banner click→apply attribution (spec §12, admin-proprietary metric): the carousel
  // appends ?ref_banner={id} when a seeker taps an employer banner. Capture it once
  // into state and scrub it from the URL (so shares/bookmarks/back-nav don't re-count),
  // then send it with the apply request.
  const [refBannerId, setRefBannerId] = useState(null);
  // Fire the Meta Pixel ViewContent conversion once per job actually viewed. fetchJob also runs on
  // owner actions (claim/edit re-fetch), which aren't seeker views — track ids we've already counted.
  const viewTrackedRef = useRef(new Set());
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref_banner');
      if (ref) {
        setRefBannerId(ref);
        params.delete('ref_banner');
        const qs = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
      }
    } catch {
      // URL APIs unavailable — attribution is best-effort, never blocks the page.
    }
  }, []);

  useEffect(() => {
    // The "more jobs" panel navigates job -> job inside this same mounted page, so every
    // per-job piece of state must reset here or job B would show job A's application.
    setMyApplication(null);
    setApplicationChecked(false);
    setMyBid(null);
    setUploadedDocuments([]);
    setLaterDocs([]);
    resumeAttachTriedRef.current = false;
    // Re-read saved files per job: something uploaded for the previous job must be offered here too.
    setSavedDocs([]);
    setSavedDocsLoaded(false);
    setCustomAnswer('');
    closeApplyDialog();
    fetchJob();
  }, [id]);

  // Check application/bid status after job is loaded
  useEffect(() => {
    if (job && profile) {
      if (isGig) {
        // For gigs, check if user has submitted a bid
        if (profile.role === 'job_seeker' || profile.role === 'seeker' || profile.role === 'contractor') {
          checkMyBid();
        }
      } else {
        // For jobs, check if user has applied
        if (profile.role === 'job_seeker' || profile.role === 'seeker') {
          checkMyApplication();
        }
      }
    }
  }, [job, profile, isGig]);

  // Fetch applications if user is the employer OR admin
  useEffect(() => {
    if (job && profile && (job.employer_id === profile.id || profile.role === 'admin')) {
      fetchApplications();
    }
  }, [job, profile]);

  // Employer banner enticement: check if THIS job already has an active banner, so we
  // only show the "feature this job" prompt when there isn't one yet.
  const isJobOwnerEmployer = !!(profile && job && job.employer_id === profile.id && isEmployerRole(profile));
  const checkMyBanners = async (currentJobId) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;
      const res = await api.get('/api/banners/mine', {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      const mine = res.data?.banners || [];
      setHasActiveBanner(mine.some((b) => b.job_id === currentJobId && b.status === 'active'));
    } catch (error) {
      console.error('Failed to check existing banners:', error);
    }
  };
  useEffect(() => {
    if (!job || isGig || !isJobOwnerEmployer) return;
    checkMyBanners(job.id);
  }, [job?.id, isGig, isJobOwnerEmployer]); // checkMyBanners intentionally omitted — it's redefined each render but stable in behavior

  const fetchJob = async () => {
    try {
      const response = await api.get(`/api/jobs/${id}`);
      const fetchedJob = response.data.job;
      setJob(fetchedJob);
      // Meta conversion: a job listing was viewed. reportJobView mirrors it server-side (so the
      // view still counts for visitors the browser pixel can't see — undecided/blocked) and fires
      // the browser pixel with a shared event id; it reports NOTHING if the visitor tapped Decline.
      // Fire at most once per job id so owner re-fetches (claim/edit) don't inflate the view signal.
      if (fetchedJob?.id && !viewTrackedRef.current.has(fetchedJob.id)) {
        viewTrackedRef.current.add(fetchedJob.id);
        reportJobView({ jobId: fetchedJob.id, jobTitle: fetchedJob.title });
      }
    } catch (error) {
      console.error('Failed to fetch job:', error);
      toast.error(t('jobDetails.loadFailed', 'Failed to load job details'));
    } finally {
      setLoading(false);
    }
  };

  // Share function using backend endpoint with proper Open Graph tags

  const handleShareToFacebook = () => {
    if (!job) return;
    
    // Use the backend share endpoint that has proper Open Graph tags
    const shareEndpoint = `${config.BACKEND_URL}/api/jobs/${id}/share`;
    
    // Check if FB SDK is loaded
    if (typeof window.FB !== 'undefined') {
      // Use Facebook SDK Share Dialog
      window.FB.ui({
        method: 'share',
        href: shareEndpoint,
        quote: `${job.title} in ${(job.locations || [job.location]).filter(Boolean).join(', ')} — ₱${job.salary_min?.toLocaleString()}-₱${job.salary_max?.toLocaleString()} | Apply now on Bohol Trabaho! #BoholJobs #scammerfree`,
      }, function(response){
        if (response && !response.error_message) {
          toast.success(t('jobDetails.sharedToFacebook', 'Shared to Facebook!'), { duration: 3000 });
        } else if (response && response.error_message) {
          toast.error(t('jobDetails.shareFailed', 'Failed to share') + ': ' + response.error_message);
          fallbackCopyLink();
        }
        // FIXED: Don't show "Share cancelled" toast when user simply closes the dialog
        // User closing the dialog is intentional, not an error
      });
    } else {
      // Fallback if FB SDK not loaded - use direct dialog
      const fbShareUrl = `https://www.facebook.com/dialog/share?app_id=1387027799794224&display=popup&href=${encodeURIComponent(shareEndpoint)}&redirect_uri=${encodeURIComponent(shareEndpoint)}`;
      
      const width = 600;
      const height = 600;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      const popup = window.open(
        fbShareUrl,
        'facebook-share-dialog',
        `width=${width},height=${height},left=${left},top=${top},toolbar=0,status=0,scrollbars=1`
      );
      
      if (popup) {
        toast.success('Opening Facebook share dialog...', { duration: 3000 });
      } else {
        fallbackCopyLink();
      }
    }
  };

  const fallbackCopyLink = () => {
    const shareUrl = `${window.location.origin}/jobs/${job?.slug || id}`;
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          toast.success('Link copied! Paste it on Facebook to share.', {
            duration: 5000
          });
        })
        .catch(() => {
          toast.error('Could not copy link. Please enable popups or copy the URL manually.');
        });
    } else {
      toast.info('Please copy the page URL from your browser to share.', {
        duration: 5000
      });
    }
  };

  // Copy shareable link - uses the backend share endpoint which has proper OG meta tags
  const handleCopyShareableLink = () => {
    if (!job) return;

    // Use the same backend share endpoint that Facebook uses for OG tags
    // This ensures the link shows proper preview when pasted anywhere (Messenger, FB Groups, etc.)
    const shareUrl = `${config.BACKEND_URL}/api/jobs/${job.slug || id}/share`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          toast.success('Link copied! Paste anywhere to share with preview.', {
            duration: 4000
          });
        })
        .catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = shareUrl;
          document.body.appendChild(textArea);
          textArea.select();
          try {
            document.execCommand('copy');
            toast.success('Link copied! Paste anywhere to share with preview.', {
              duration: 4000
            });
          } catch (err) {
            toast.error('Could not copy link. Please copy the URL manually.');
          }
          document.body.removeChild(textArea);
        });
    } else {
      toast.info(`Share this link: ${shareUrl}`, {
        duration: 8000
      });
    }
  };

  // Share to Viber
  const handleShareToViber = () => {
    if (!job) return;
    const shareUrl = `${config.BACKEND_URL}/api/jobs/${job.slug || id}/share`;
    const shareText = `${job.title} in ${(job.locations || [job.location]).filter(Boolean).join(', ')} — ₱${job.salary_min?.toLocaleString()}-₱${job.salary_max?.toLocaleString()} | Apply now on Bohol Trabaho!`;
    const viberUrl = `viber://forward?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
    window.open(viberUrl, '_blank');
    toast.success(t('jobDetails.openingViber') || 'Opening Viber...', { duration: 2000 });
  };

  // Share to Messenger
  const handleShareToMessenger = () => {
    if (!job) return;
    const shareUrl = `${config.BACKEND_URL}/api/jobs/${job.slug || id}/share`;
    const messengerUrl = `fb-messenger://share/?link=${encodeURIComponent(shareUrl)}`;
    window.open(messengerUrl, '_blank');
    toast.success(t('jobDetails.openingMessenger') || 'Opening Messenger...', { duration: 2000 });
  };

  const checkMyApplication = async () => {
    if (!profile || !job) return;

    try {
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .eq('job_id', job.id) // Use job.id (UUID) not URL param
        .eq('seeker_id', profile.id)
        .limit(1);

      if (!error) {
        setMyApplication(data && data.length > 0 ? data[0] : null);
      }
    } catch (error) {
      console.error('Failed to check application:', error);
    } finally {
      setApplicationChecked(true);
    }
  };

  const checkMyBid = async () => {
    if (!profile || !job) return;

    try {
      const { data, error } = await supabase
        .from('bids')
        .select('*')
        .eq('gig_id', job.id) // Use job.id (UUID) since gigs are in jobs table
        .eq('contractor_id', profile.id)
        .maybeSingle();

      if (!error && data) {
        setMyBid(data);
      }
    } catch (error) {
      console.error('Failed to check bid:', error);
    }
  };

  // Sort: unviewed first, then by created_at DESC
  const sortApplications = (items) => (items || []).sort((a, b) => {
    const aNew = !a.viewed_at;
    const bNew = !b.viewed_at;
    if (aNew !== bNew) return aNew ? -1 : 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const fetchApplications = async () => {
    setLoadingApplications(true);
    let paintedFromCache = false;
    try {
      // Stale-while-revalidate (same pattern as the jobs table): paint the cached
      // applicants instantly, then refresh from the network and swap in fresh data.
      // Jobs only — gig bids aren't cached (gigs are disabled).
      if (!isGig) {
        try {
          const cached = await getApplicationsFromCache(job.id);
          if (cached && cached.length) {
            setApplications(sortApplications(cached));
            setLoadingApplications(false);
            paintedFromCache = true;
          }
        } catch {
          // Cache is best-effort — fall through to the network.
        }
      }

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      // For gigs, fetch bids. For jobs, fetch applications
      const endpoint = isGig
        ? `/api/bids/gig/${job.id}`
        : `/api/applications/job/${id}`;

      const response = await api.get(endpoint, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      // Get the right data key (bids or applications)
      const items = isGig ? response.data.bids : response.data.applications;
      const sorted = sortApplications(items);
      setApplications(sorted);
      if (!isGig) {
        saveApplicationsToCache(job.id, sorted).catch(() => {});
      }
    } catch (error) {
      console.error(`Failed to fetch ${isGig ? 'bids' : 'applications'}:`, error);
      // If the cache painted, stale data stays on screen — better than empty.
      if (paintedFromCache) return;
    } finally {
      setLoadingApplications(false);
    }
  };

  // Quick answer for one job applicant: "Moving forward" / "Not a fit this time".
  // The backend alerts the applicant (in-app + push, never SMS) only when the answer changes.
  const setApplicationDecision = async (application, decision) => {
    if (decidingId) return;
    setDecidingId(application.id);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error(t('common.pleaseLogin', 'Please log in'));
        return;
      }
      const response = await api.post(`/api/applications/${application.id}/decision`, { decision }, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      const newStatus = response.data?.status;
      if (newStatus) {
        setApplications(prev =>
          prev.map(app =>
            app.id === application.id
              ? { ...app, status: newStatus, viewed_at: app.viewed_at || new Date().toISOString() }
              : app
          )
        );
      }
      if (response.data?.changed === false) {
        toast.success(t('dashboard.decisionSaved', 'Answer saved'));
      } else {
        toast.success(decision === 'moving_forward'
          ? t('dashboard.decisionMovingForwardSent', 'Applicant told you are moving forward')
          : t('dashboard.decisionNotAFitSent', 'Applicant told, kindly, that it is not a fit this time'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('dashboard.decisionFailed', 'Could not save your answer. Please try again.'));
    } finally {
      setDecidingId(null);
    }
  };

  const markAsViewed = async (applicationId) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;
      const endpoint = isGig
        ? `/api/bids/${applicationId}/view`
        : `/api/applications/${applicationId}/view`;
      await api.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setApplications(prev =>
        prev.map(app =>
          app.id === applicationId
            ? { ...app, viewed_at: new Date().toISOString() }
            : app
        )
      );
    } catch (error) {
      console.error('Failed to mark as viewed:', error);
    }
  };

  const loadNextJobs = async (excludeId) => {
    const requestId = ++nextJobsRequestRef.current;
    const isCurrent = () => requestId === nextJobsRequestRef.current;
    setNextJobsLoading(true);
    setNextJobs([]);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) return;
      const res = await api.get('/api/applications/next-jobs', {
        params: { exclude: excludeId, limit: 3 },
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000, // a cold recommendation cache can take a few seconds to build
      });
      if (isCurrent()) setNextJobs(res.data?.jobs || []);
    } catch (error) {
      console.warn('[JobDetails] next-jobs unavailable:', error?.message);
      if (isCurrent()) setNextJobs([]);
    } finally {
      if (isCurrent()) setNextJobsLoading(false);
    }
  };

  // Apply to one of the suggested jobs from the panel. The panel has already let the seeker
  // review/edit the pre-filled cover letter and answer any screening question; this just
  // submits. Returns true when the job should now show as applied.
  const applyToSuggestedJob = async (sJob, { cover: sCover, customAnswer: sAnswer }) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error(t('jobDetails.pleaseLoginApply', 'Please log in to apply'));
        return false;
      }
      const res = await api.post(
        '/api/applications',
        {
          job_id: sJob.id,
          cover: sCover,
          custom_answer: sJob.custom_question ? sAnswer : null,
          documents: null,
          ...applyTrackingFields(),
        },
        { headers: { Authorization: `Bearer ${session.session.access_token}` } }
      );
      // Same conversion signal as the main apply path; app-<id> event id dedups with the
      // server-side copy.
      trackPixel('SubmitApplication', {
        content_type: 'job',
        content_ids: [sJob.id],
        content_name: sJob.title,
      }, applicationEventId(res));
      setNextApplied((prev) => new Set(prev).add(sJob.id));
      toast.success(t('jobDetails.nextJobsSentTo', 'Application sent to {{title}}', { title: sJob.title }));
      return true;
    } catch (error) {
      const errorDetail = error.response?.data?.detail || '';
      if (error.response?.status === 400 && errorDetail.includes('already applied')) {
        setNextApplied((prev) => new Set(prev).add(sJob.id));
        return true;
      }
      if (error.response?.status === 400 && errorDetail.includes('Phone number is required')) {
        toast.error(t('jobDetails.phoneRequired', 'Please add your phone number to apply for jobs'));
        closeApplyDialog();
        rememberReturnTo(window.location.pathname, job?.title);
        setTimeout(() => navigate('/complete-profile'), 500);
        return false;
      }
      toast.error(errorDetail || t('jobDetails.applicationFailed', 'Failed to submit application'));
      return false;
    }
  };

  // ---- keep an application in progress: draft, saved files, phone inside the pop-up ----------
  const draftKey = job && profile ? `${profile.id}:${job.id}` : null;
  const ownDoc = (d) => d && typeof d.path === 'string' && profile && d.path.startsWith(`${profile.id}/`);
  const needsPhone = !!profile && !(profile.phone && profile.phone.trim());

  // Open the apply form with whatever this person had already written for this job.
  const openApplyDialog = () => {
    const draft = draftKey ? loadApplyDraft(draftKey) : null;
    setCover(draft?.cover || '');
    setCustomAnswer(draft?.customAnswer || '');
    const draftDocs = (draft?.documents || []).filter(ownDoc);
    if (draftDocs.length) {
      setUploadedDocuments((prev) => {
        const names = new Set(prev.map((d) => d.name));
        return [...prev, ...draftDocs.filter((d) => !names.has(d.name))];
      });
    }
    setApplyStep('form');
    setShowApplyDialog(true);
  };

  // Autosave while the form is open, so closing it (or leaving for the profile page) loses nothing.
  useEffect(() => {
    if (!showApplyDialog || applyStep !== 'form' || !draftKey) return;
    saveApplyDraft(draftKey, { cover, customAnswer, documents: uploadedDocuments.filter(ownDoc) });
  }, [showApplyDialog, applyStep, cover, customAnswer, uploadedDocuments, draftKey]);

  // Load the seeker's saved files once the form opens on a job that asks for documents.
  useEffect(() => {
    if (!showApplyDialog || applyStep !== 'form' || savedDocsLoaded) return;
    if (!job?.required_documents?.length || !profile) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        const token = session?.session?.access_token;
        if (!token) return;
        const res = await api.get('/api/application-documents/mine', { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        setSavedDocs((res.data?.documents || []).filter(ownDoc));
        setSavedResume(res.data?.resume || null);
      } catch (e) {
        /* saved files are a convenience — the normal upload still works */
      } finally {
        if (!cancelled) setSavedDocsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [showApplyDialog, applyStep, savedDocsLoaded, job, profile]);

  // Pre-fill each requested document from a saved file with the same name; a "Resume" slot with
  // no saved match is filled from the resume on their profile (copied server-side, once).
  useEffect(() => {
    if (!showApplyDialog || applyStep !== 'form' || !savedDocsLoaded || !job?.required_documents?.length) return;
    const have = new Set(uploadedDocuments.map((d) => d.name));
    const additions = [];
    let resumeSlot = null;
    job.required_documents.forEach((doc) => {
      if (!doc?.name || have.has(doc.name)) return;
      let match = findSavedMatch(doc.name, savedDocs);
      // A resume re-uploaded on the profile since the saved copy was made wins over the old copy.
      if (match && isResumeDoc(doc.name) && savedResume?.uploaded_at
          && match.uploaded_at && new Date(savedResume.uploaded_at) > new Date(match.uploaded_at)) {
        match = null;
      }
      if (match) additions.push({ name: doc.name, path: match.path, filename: `${match.name} (saved)` });
      else if (!resumeSlot && isResumeDoc(doc.name) && savedResume) resumeSlot = doc.name;
    });
    if (additions.length) setUploadedDocuments((prev) => [...prev.filter((d) => !additions.some((a) => a.name === d.name)), ...additions]);
    if (resumeSlot && !resumeAttachTriedRef.current) {
      resumeAttachTriedRef.current = true;
      (async () => {
        try {
          const { data: session } = await supabase.auth.getSession();
          const token = session?.session?.access_token;
          if (!token) return;
          const res = await api.post('/api/application-documents/from-resume', { document_name: resumeSlot },
            { headers: { Authorization: `Bearer ${token}` } });
          const doc = res.data?.document;
          if (doc && ownDoc(doc)) {
            setUploadedDocuments((prev) => (prev.some((d) => d.name === resumeSlot) ? prev
              : [...prev, { name: resumeSlot, path: doc.path, filename: `${savedResume.filename || 'Resume'} (from your profile)` }]));
          }
        } catch (e) {
          /* fall back to a normal upload */
        }
      })();
    }
  }, [showApplyDialog, applyStep, savedDocsLoaded, savedDocs, savedResume, job]);

  const pickSavedDocument = (docName, path) => {
    const saved = savedDocs.find((d) => d.path === path);
    if (!saved) return;
    setUploadedDocuments((prev) => [...prev.filter((d) => d.name !== docName),
      { name: docName, path: saved.path, filename: `${saved.name} (saved)` }]);
  };

  // Coming back from the profile page (or sign-up) with ?apply=1: reopen the form, draft included.
  useEffect(() => {
    if (!job || !profile || !applicationChecked || showApplyDialog) return;
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    if (params.get('apply') !== '1') return;
    params.delete('apply');
    const rest = params.toString();
    window.history.replaceState(window.history.state, '', window.location.pathname + (rest ? `?${rest}` : ''));
    const isOwnJob = job.employer_id === profile.id;
    const canApply = (profile.role === 'job_seeker' || profile.role === 'seeker') && !isOwnJob
      && job.post_type !== 'gig' && !(job.source === 'community_board' && !job.is_claimed);
    if (canApply && !myApplication) openApplyDialog();
  }, [job, profile, applicationChecked, myApplication]);

  // Save a phone typed inside the pop-up to the profile (same field the profile screen writes).
  const savePhoneFromDialog = async () => {
    const raw = phoneInput.trim();
    const digits = normalizePhonePH(raw);
    if (!/^639\d{9}$/.test(digits)) {
      toast.error(t('jobDetails.phoneInvalid', 'Please enter a valid mobile number, e.g. 0917 123 4567'));
      return false;
    }
    const { error } = await supabase.from('profiles').update({ phone: raw, updated_at: new Date().toISOString() }).eq('id', profile.id);
    if (error) {
      toast.error(t('jobDetails.phoneSaveFailed', 'Could not save your phone number. Please try again.'));
      return false;
    }
    if (setProfile) setProfile({ ...profile, phone: raw });
    return true;
  };

  // Leave for the profile page without losing anything: the draft is already saved; remember
  // the job so the profile page offers the way back and the form reopens on return.
  const goToProfileKeepingApplication = () => {
    if (draftKey) saveApplyDraft(draftKey, { cover, customAnswer, documents: uploadedDocuments.filter(ownDoc) });
    rememberReturnTo(`${window.location.pathname}?apply=1`, job?.title);
    setShowApplyDialog(false);
    navigate('/profile');
    // The app keeps the job page's scroll position; start the profile at the top so the
    // "Continue your application" card (the way back) is the first thing they see.
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const closeApplyDialog = () => {
    nextJobsRequestRef.current += 1; // any in-flight next-jobs response is now stale
    setShowApplyDialog(false);
    setApplyStep('form');
    setNextJobs([]);
  };

  const handleApply = async () => {
    // No phone on the profile yet: it's asked for right here in the pop-up (no redirect).
    if (needsPhone) {
      if (!phoneInput.trim()) {
        toast.error(t('jobDetails.phoneRequired', 'Please add your phone number to apply for jobs'));
        return;
      }
    }

    // Validate custom question answer if required
    if (job?.custom_question && !customAnswer.trim()) {
      toast.error(t('jobDetails.answerRequired', 'Please answer the employer\'s screening question'));
      return;
    }

    // Validate required documents
    const requiredDocs = job?.required_documents || [];
    if (requiredDocs.length > 0) {
      const requiredNames = requiredDocs.filter(d => d.required !== false).map(d => d.name);
      const uploadedNames = uploadedDocuments.map(d => d.name);
      const missingDocs = requiredNames.filter(name => !uploadedNames.includes(name));
      if (missingDocs.length > 0) {
        toast.error(`Please upload required documents: ${missingDocs.join(', ')}`);
        return;
      }
    }

    setApplying(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error(t('jobDetails.pleaseLoginApply', 'Please log in to apply'));
        return;
      }
      if (needsPhone && !(await savePhoneFromDialog())) return;

      const applyRes = await api.post(
        '/api/applications',
        {
          job_id: job.id,
          cover,
          custom_answer: job?.custom_question ? customAnswer : null,
          documents: uploadedDocuments.length > 0 ? uploadedDocuments : null,
          banner_id: refBannerId || undefined,
          ...applyTrackingFields(),
        },
        {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`
          }
        }
      );

      // Meta Pixel conversion: a real job application was submitted (the goal, not just a signup).
      // Guarded — no-ops until the visitor accepted analytics/marketing cookies. The backend
      // also reports it server-side under the same app-<id> event id, so Meta counts it once.
      trackPixel('SubmitApplication', {
        content_type: 'job',
        content_ids: [job.id],
        content_name: job.title,
      }, applicationEventId(applyRes));
      if (draftKey) clearApplyDraft(draftKey);
      // Applied: this job no longer needs a "Continue your application" reminder.
      const pendingReturn = peekReturnTo();
      if (pendingReturn && pendingReturn.split('?')[0] === window.location.pathname) clearReturnTo();

      // Keep the dialog open and switch it to the "more jobs that fit you" step — the moment
      // right after applying is when a second and third application are most likely.
      setApplyStep('next');
      loadNextJobs(job.id);
      await checkMyApplication();
    } catch (error) {
      console.error('Apply error:', error);
      const errorDetail = error.response?.data?.detail || '';

      if (error.response?.status === 400 && errorDetail.includes('already applied')) {
        toast.error(t('jobDetails.alreadyApplied', 'You have already applied to this job'));
        await checkMyApplication();
      } else if (error.response?.status === 400 && errorDetail.includes('Phone number is required')) {
        // The profile has no phone after all: ask for it right here instead of leaving the form
        if (setProfile) setProfile({ ...profile, phone: '' });
        toast.error(t('jobDetails.phoneRequired', 'Please add your phone number to apply for jobs'));
      } else {
        toast.error(errorDetail || t('jobDetails.applicationFailed', 'Failed to submit application'));
      }
    } finally {
      setApplying(false);
    }
  };

  // Community Board apply - logs application then opens mailto
  const handleCBApply = async () => {
    setCbApplying(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in to apply');
        return;
      }

      const res = await api.post(
        '/api/community-board/apply',
        { job_id: job.id, cover: cbCover, ...applyTrackingFields() },
        { headers: { Authorization: `Bearer ${session.session.access_token}` } }
      );

      // Meta Pixel conversion: a Community Board application was submitted. Same goal signal as the
      // in-app apply path. Guarded — no-ops until the visitor accepted analytics/marketing cookies.
      // Shares the backend's app-<id> event id so the server-side copy dedups with this one.
      trackPixel('SubmitApplication', {
        content_type: 'job',
        content_ids: [job.id],
        content_name: job.title,
      }, applicationEventId(res));

      const contactEmail = res.data.contact_email;
      const applicantName = profile?.name || '';
      const applicantPhone = profile?.phone || '';

      // Build mailto body
      const subject = `Application: ${job.title} - Bohol Trabaho`;
      const bodyParts = [
        `Hi,`,
        ``,
        `I'm interested in the "${job.title}" position posted on Bohol Trabaho.`,
        ``,
        ...(cbCover ? [cbCover, ''] : []),
        `Name: ${applicantName}`,
        `Phone: ${applicantPhone}`,
        ``,
        `Thank you!`
      ];
      const body = bodyParts.join('\n');

      const mailtoUrl = `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      setShowCBApplyDialog(false);
      await checkMyApplication();
      toast.success('Application logged! Opening your email app...');

      // Open mailto
      window.location.href = mailtoUrl;
    } catch (error) {
      console.error('CB apply error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setCbApplying(false);
    }
  };

  // Re-open mailto for CB job (resend)
  const handleCBResend = () => {
    if (!job || !myApplication) return;
    // We don't have contact_email in the public response (it's stripped),
    // so we call the apply endpoint which returns it for existing applications
    handleCBApply();
  };

  // Claim flow handlers
  const handleClaimRequest = async () => {
    setClaimLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in to claim this listing');
        return;
      }
      const response = await api.post(`/api/jobs/${job.id}/claim/request`, {}, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setClaimEmail(response.data.masked_email || '');
      setClaimStep('verify');
      toast.success('Verification code sent! Check the employer email inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setClaimLoading(false);
    }
  };

  const handleClaimVerify = async () => {
    if (!claimCode || claimCode.length !== 6) {
      toast.error('Please enter the 6-digit verification code');
      return;
    }
    setClaimLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in to claim this listing');
        return;
      }
      await api.post(`/api/jobs/${job.id}/claim/verify`, { code: claimCode }, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      toast.success('Listing claimed successfully! This job is now on your account.');
      setShowClaimDialog(false);
      setClaimStep('request');
      setClaimCode('');
      // Refresh the job to reflect claimed status
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setClaimLoading(false);
    }
  };

  const handleDocumentUpload = async (e, docName) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingDoc(docName);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in to upload documents');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_name', docName);

      const response = await api.post('/api/upload/application-document', formData, {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
          // Do NOT set Content-Type manually — Axios auto-sets it with the
          // correct multipart boundary when given FormData
        }
      });

      if (response.data?.success) {
        // Add or replace document in uploaded list
        setUploadedDocuments(prev => {
          const filtered = prev.filter(d => d.name !== docName);
          return [...filtered, response.data.document];
        });
        toast.success(`${docName} uploaded successfully`);
      }
    } catch (error) {
      console.error('Document upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleBid = async () => {
    if (!bidData.amount || !bidData.timeline) {
      toast.error(t('jobDetails.fillRequiredFields', 'Please fill in all required fields'));
      return;
    }

    setBidding(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error(t('jobDetails.pleaseLoginBid', 'Please log in to submit a bid'));
        return;
      }

      await api.post(
        '/api/bids',
        {
          gig_id: job.id, // job.id since gigs are in jobs table
          amount: parseFloat(bidData.amount),
          timeline: bidData.timeline,
          description: bidData.description
        },
        {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`
          }
        }
      );

      setShowBidDialog(false);
      setBidData({ amount: '', timeline: '', description: '' });
      await checkMyBid();
      toast.success(t('jobDetails.bidSuccess', 'Bid submitted successfully! The employer will review your bid.'), { duration: 5000 });
    } catch (error) {
      console.error('Bid error:', error);
      const errorDetail = error.response?.data?.detail || '';

      if (error.response?.status === 400 && errorDetail.includes('already')) {
        toast.error(t('jobDetails.alreadyBid', 'You have already submitted a bid for this gig'));
        await checkMyBid();
      } else if (error.response?.status === 400 && errorDetail.includes('Phone number is required')) {
        // Phone number missing - redirect to profile completion
        toast.error(t('jobDetails.phoneRequiredBid', 'Please add your phone number to submit bids'));
        setShowBidDialog(false);
        setTimeout(() => navigate('/complete-profile'), 500);
      } else {
        toast.error(errorDetail || t('jobDetails.bidFailed', 'Failed to submit bid'));
      }
    } finally {
      setBidding(false);
    }
  };

  const handleExportApplicants = async () => {
    setExporting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in');
        return;
      }

      const response = await api.get(`/api/jobs/${job.id}/export-applicants`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      try {
        const link = document.createElement('a');
        link.href = url;

        // Get filename from response headers or use default
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'Applicants.xlsx';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(t('job.exportSuccess', 'Applicants exported successfully!'));
      } finally {
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export error:', error);
      const errorMessage = error.response?.data?.detail || t('job.exportFailed', 'Failed to export applicants');
      toast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in');
        return;
      }

      await api.delete(`/api/jobs/${id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      toast.success(t('job.deleteSuccess', 'Job deleted successfully'));
      navigate('/jobs');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || t('job.deleteFailed', 'Failed to delete job'));
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleEditSuccess = (updatedJob) => {
    setJob({ ...job, ...updatedJob });
    toast.success(t('job.updateSuccess', 'Job updated!'));
    const newSlug = updatedJob.slug || updatedJob.id;
    if (newSlug && newSlug !== slug) {
      navigate(`/jobs/${newSlug}`, { replace: true });
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center">
        <p>Job not found</p>
      </div>
    );
  }

  const isJobSeeker = profile?.role === 'job_seeker';
  const isPesoStaff = profile?.role === 'peso_staff'; // review-only: can browse postings, cannot apply
  const isEmployer = profile?.role === 'employer' && job.employer_id === profile.id;
  const canViewApplications = isEmployer || isAdmin; // Admin can also view applications
  const hasApplied = !!myApplication;
  const canChat = myApplication && (myApplication.status === 'accepted' || myApplication.status === 'revealed');
  const canWithdraw = myApplication && (myApplication.status === 'applied' || myApplication.status === 'pending');
  const isCommunityBoard = job?.source === 'community_board' && !job?.is_claimed;

  // Generate SEO schema for job posting
  const jobSchema = job ? generateJobPostingSchema(job, job.employer) : null;
  const breadcrumbSchema = job ? generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Jobs', url: '/jobs' },
    { name: job.title }
  ]) : null;
  
  // Combine schemas
  const combinedSchema = jobSchema && breadcrumbSchema ? [jobSchema, breadcrumbSchema] : jobSchema;
  
  // Format salary for SEO description
  const formatSalaryRange = () => {
    if (!job) return '';
    const min = job.salary_min ? `₱${job.salary_min.toLocaleString()}` : '';
    const max = job.salary_max ? `₱${job.salary_max.toLocaleString()}` : '';
    if (min && max) return `${min}-${max}`;
    return min || max || '';
  };

  const getMatchColor = (score) => {
    if (score >= 75) return 'bg-green-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      {/* SEO: Dynamic meta tags and JobPosting schema for Google Jobs */}
      {job && (
        <SEO
          title={`${job.title} in ${(job.locations || [job.location]).filter(Boolean).join(', ')}`}
          description={`${job.title} - ${formatSalaryRange()}${job.salary_type ? `/${job.salary_type}` : ''} in ${(job.locations || [job.location]).filter(Boolean).join(', ')}, Bohol. ${job.description?.substring(0, 120) || 'Apply now on Bohol Trabaho!'}`}
          url={`/jobs/${job.slug || job.id}`}
          type="website"
          schema={combinedSchema}
          keywords={[
            job.title,
            `${job.title} job`,
            `jobs in ${(job.locations || [job.location]).filter(Boolean).join(', ')}`,
            'Bohol jobs',
            'trabaho sa Bohol',
            job.employer?.business_name,
            'hiring Bohol'
          ].filter(Boolean)}
          publishedTime={job.created_at}
          modifiedTime={job.updated_at}
        />
      )}
      
      <TopBar profile={profile} setProfile={setProfile} />

      <div className="px-4 py-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft size={16} className="mr-2" />
            {t('common.back')}
          </Button>
          
          <div className="flex items-center gap-2">
            {profile?.role === 'admin' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.print()}
                className="rounded-xl no-print"
              >
                <Printer size={16} className="mr-2" />
                Print
              </Button>
            )}
            {profile && job && profile.id !== job.employer_id && (
              <ReportButton itemType="job" itemId={job.id} />
            )}
          </div>
        </div>

        <Card>
          <CardContent className="p-6">
            {/* Job Image */}
            {job.image_url && (
              <div className="mb-6 -mx-6 -mt-6 cursor-pointer" onClick={() => setLightboxOpen(true)}>
                <img
                  src={fixImageUrl(job.image_url)}
                  alt={job.title}
                  className="w-full h-64 object-cover rounded-t-lg"
                  style={{ objectPosition: `center ${job.image_position ?? 50}%` }}
                />
              </div>
            )}

            {/* Edit/Delete Buttons (Owner or Admin) */}
            {canEdit && (
              <div className="flex gap-2 mb-4 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEditModal(true)}
                  data-testid="edit-job-btn"
                >
                  <Edit size={16} className="mr-2" />
                  {t('common.edit', 'Edit')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                  data-testid="delete-job-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  {t('common.delete', 'Delete')}
                </Button>
              </div>
            )}

            <div className="mb-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h1 className="font-heading text-3xl font-bold mb-2">{getLocalizedField(job, 'title')}</h1>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    {isCommunityBoard ? (
                      <span className="flex items-center gap-1.5">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded bg-cyan-100 text-cyan-700 text-[10px] font-bold">CB</span>
                        <span>Community Board</span>
                      </span>
                    ) : job.employer && (
                      <button
                        onClick={() => navigate(`/employers/${job.employer.id}`)}
                        className="flex items-center gap-1 hover:text-primary transition-colors cursor-pointer group"
                      >
                        {job.employer.verified && <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />}
                        <span className="group-hover:underline">
                          {job.employer.business_name || job.employer.name || 'Anonymous Employer'}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
                {job.employer?.rating > 0 && (
                  <button
                    onClick={() => setShowReviewsModal(true)}
                    className="transition-transform hover:scale-105 cursor-pointer"
                  >
                    <Badge className="text-lg">⭐ {job.employer.rating.toFixed(1)}</Badge>
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-6">
                <div className="flex items-center text-sm">
                  <MapPin size={16} className="mr-2 text-muted-foreground flex-shrink-0" />
                  <span>{(job.locations || [job.location]).filter(Boolean).join(', ')}</span>
                </div>
                {(job.salary_min || job.salary_max) ? (
                  <div className="flex items-center text-sm">
                    <span className="mr-2 text-muted-foreground font-semibold">₱</span>
                    {job.salary_min?.toLocaleString() || '0'} - ₱{job.salary_max?.toLocaleString() || '0'}
                    {job.salary_period && <span className="text-muted-foreground ml-1">per {job.salary_period === 'daily' ? 'day' : 'month'}</span>}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Salary not disclosed</span>
                )}
                <Badge variant="secondary">{job.type?.replace('_', ' ')}</Badge>
                {isCommunityBoard && (
                  <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 font-medium">
                    Community Board
                  </span>
                )}

                {/* Quick Filter Badges */}
                {job.positions_available > 1 && (
                  <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-medium dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800">
                    <Users size={14} /> Hiring {job.positions_available}
                  </span>
                )}
                {job.urgent_hiring && (
                  <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium dark:bg-red-950 dark:text-red-300 dark:border-red-800">
                    🔴 Urgent Hiring
                  </span>
                )}
                {job.no_experience_needed && (
                  <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium dark:bg-green-950 dark:text-green-300 dark:border-green-800">
                    ⭐ No Experience Needed
                  </span>
                )}
                {job.with_housing && (
                  <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                    🏠 Housing Provided
                  </span>
                )}
              </div>
            </div>

            <div className="mb-6">
              <h2 className="font-heading text-xl font-semibold mb-3">{t('jobDetails.description')}</h2>
              <div
                className="text-muted-foreground prose prose-sm max-w-none overflow-x-auto break-words"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getLocalizedField(job, 'description') || '') }}
              />
            </div>

            {job.benefits && job.benefits.length > 0 && (
              <div className="mb-6" data-testid="job-benefits-section">
                <h2 className="font-heading text-xl font-semibold mb-3">{t('jobDetails.benefits')}</h2>
                <div className="flex flex-wrap gap-2">
                  {job.benefits.map((benefit, i) => (
                    <Badge
                      key={i}
                      variant="secondary"
                      className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20"
                      data-testid={`job-benefit-${i}`}
                    >
                      {benefit}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {displayedRequirements && displayedRequirements.length > 0 && (
              <div className="mb-6">
                <h2 className="font-heading text-xl font-semibold mb-3">{t('jobDetails.requirements')}</h2>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  {displayedRequirements.map((req, i) => (
                    <li key={i}>{req}</li>
                  ))}
                  {/* Show required documents in requirements list */}
                  {jobDocLines}
                </ul>
              </div>
            )}

            {/* Show required documents section if no other requirements but documents exist */}
            {(!(displayedRequirements && displayedRequirements.length > 0)) && job.required_documents && job.required_documents.length > 0 && (
              <div className="mb-6">
                <h2 className="font-heading text-xl font-semibold mb-3">{t('jobDetails.requirements')}</h2>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  {jobDocLines}
                </ul>
              </div>
            )}

            {/* Community Board notice */}
            {isCommunityBoard && (
              <div className="border-t pt-6 mb-4">
                <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 text-sm">
                  <p className="text-cyan-800 font-medium mb-1">Community Board Listing</p>
                  <p className="text-cyan-700">
                    This job was found on social media and curated for you. Applying will open your email app to contact the employer directly. Attach your resume to the email.
                  </p>
                </div>
              </div>
            )}

            {/* Claim This Listing - for employers on unclaimed community board jobs */}
            {isCommunityBoard && profile?.role === 'employer' && (
              <div className="mb-4">
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-emerald-800 font-medium mb-2">
                      Is this your job post?
                    </p>
                    <p className="text-xs text-emerald-700 mb-3">
                      If you originally posted this job, claim it to manage applications directly on Bohol Trabaho.
                    </p>
                    <Button
                      size="sm"
                      className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => {
                        if (!profile) {
                          rememberReturnTo(window.location.pathname + window.location.search, job.title);
                          navigate('/auth');
                        } else {
                          setClaimStep('request');
                          setClaimCode('');
                          setClaimEmail('');
                          setShowClaimDialog(true);
                        }
                      }}
                    >
                      <Shield size={16} className="mr-2" />
                      Claim This Listing
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Apply/Bid Actions - Hidden for employers and PESO staff (review-only) */}
            {!isEmployer && !isPesoStaff && !hasApplied && !myBid && (
              <div className={`${isCommunityBoard ? '' : 'border-t'} pt-6 space-y-3`}>
                {/* For Jobs: Apply button */}
                {!isGig && (
                  <Button
                    size="lg"
                    className="w-full rounded-xl"
                    onClick={() => {
                      if (!profile) {
                        reportApplyIntent({ jobId: job.id, postType: job.post_type });
                        rememberReturnTo(window.location.pathname + window.location.search, job.title);
                        navigate('/auth');
                      } else if (isCommunityBoard && (!profile.phone || profile.phone.trim() === '')) {
                        toast.error('Please add your phone number so employers can reach you for an interview. Update your profile to continue.');
                        rememberReturnTo(window.location.pathname, job.title);
                        setTimeout(() => navigate('/complete-profile'), 500);
                      } else if (isCommunityBoard) {
                        setCbCover('');
                        setShowCBApplyDialog(true);
                      } else {
                        openApplyDialog();
                      }
                    }}
                    data-testid="apply-now-btn"
                  >
                    <Send size={16} className="mr-2" />
                    {t('jobDetails.applyNow')}
                  </Button>
                )}
                
                {/* For Gigs: Submit Bid button */}
                {isGig && (
                  <Button
                    size="lg"
                    className="w-full rounded-xl"
                    onClick={() => {
                      if (!profile) {
                        reportApplyIntent({ jobId: job.id, postType: job.post_type });
                        rememberReturnTo(window.location.pathname + window.location.search, job.title);
                        navigate('/auth');
                      } else if (!profile.phone || profile.phone.trim() === '') {
                        // Phone number required to bid - clients need to contact contractors
                        toast.error('Please add your phone number so clients can reach you if your bid is selected. Update your profile to continue.');
                        setTimeout(() => navigate('/complete-profile'), 500);
                      } else {
                        setShowBidDialog(true);
                      }
                    }}
                    data-testid="submit-bid-btn"
                  >
                    <Send size={16} className="mr-2" />
                    Submit Bid
                  </Button>
                )}
                
                {/* Save button - Show to everyone. Redirect to auth if not logged in */}
                {isSeeker && (
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full rounded-xl"
                    onClick={() => {
                      if (!profile) {
                        rememberReturnTo(window.location.pathname + window.location.search, job.title);
                        navigate('/auth');
                      } else {
                        toggleSaveJob(job.id);
                      }
                    }}
                    data-testid="save-job-btn"
                  >
                    <Heart 
                      size={18} 
                      className={`mr-2 ${isSaved ? 'fill-red-500 text-red-500' : ''}`}
                    />
                    {isSaved ? t('jobs.saved') : t('jobDetails.saveJob')}
                  </Button>
                )}
              </div>
            )}
            
            {/* PESO staff: review-only - they browse postings but cannot apply */}
            {isPesoStaff && (
              <div className="border-t pt-6">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 text-center text-sm text-blue-700">
                    {t('jobDetails.pesoViewOnly', 'PESO staff view: you can review this posting; applying is for job seekers.')}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Already Applied/Bid Message - Show for seeker role (not job_seeker) */}
            {!isEmployer && !isJobSeeker && (hasApplied || myBid) && (
              <div className="border-t pt-6">
                <Card className={`${isCommunityBoard ? 'bg-cyan-50 border-cyan-200' : 'bg-blue-50 border-blue-200'}`}>
                  <CardContent className="p-6 text-center">
                    <div className={`${isCommunityBoard ? 'text-cyan-700' : 'text-blue-600'} font-semibold mb-2`}>
                      {hasApplied && myApplication?.created_at
                        ? `Application sent ${new Date(myApplication.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                        : `You have already ${isGig ? 'submitted a bid for' : 'applied for'} this ${isGig ? 'gig' : 'job'}`}
                    </div>
                    {isCommunityBoard && hasApplied ? (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Didn't send the email? Click below to open it again.
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-cyan-300 text-cyan-700 hover:bg-cyan-100"
                          onClick={handleCBResend}
                          disabled={cbApplying}
                        >
                          <Send size={14} className="mr-2" />
                          {cbApplying ? 'Opening...' : 'Resend Application Email'}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Your {isGig ? 'bid' : 'application'} is being reviewed by the {isGig ? 'owner' : 'employer'}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Share Button with Dropdown - Show to ALL users (including employers) */}
            <div className={`${!isEmployer ? '' : 'border-t'} pt-6`}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full rounded-xl border-[#1877F2] text-[#1877F2] hover:bg-[#1877F2]/10 transition-colors"
                    data-testid="share-dropdown-btn"
                  >
                    <Share2 size={18} className="mr-2" />
                    {t('jobDetails.share') || 'Share'}
                    <ChevronDown size={16} className="ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-[280px]">
                  <DropdownMenuItem
                    onClick={handleShareToFacebook}
                    className="cursor-pointer py-3"
                    data-testid="share-facebook-btn"
                  >
                    <Facebook size={18} className="mr-3 text-[#1877F2]" fill="#1877F2" />
                    <span>{t('jobDetails.shareToFacebook') || 'Share to Facebook'}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleShareToMessenger}
                    className="cursor-pointer py-3"
                    data-testid="share-messenger-btn"
                  >
                    <MessageSquare size={18} className="mr-3 text-[#0099FF]" />
                    <span>{t('jobDetails.shareToMessenger') || 'Share to Messenger'}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleShareToViber}
                    className="cursor-pointer py-3"
                    data-testid="share-viber-btn"
                  >
                    <Phone size={18} className="mr-3 text-[#7360F2]" />
                    <span>{t('jobDetails.shareToViber') || 'Share to Viber'}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleCopyShareableLink}
                    className="cursor-pointer py-3"
                    data-testid="copy-link-btn"
                  >
                    <Link2 size={18} className="mr-3 text-gray-600" />
                    <span>{t('jobDetails.copyLink') || 'Copy Shareable Link'}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Your Application Card - FIXED button logic */}
            {isJobSeeker && hasApplied && (
              <div className="border-t pt-6">
                <Card className={isCommunityBoard ? 'bg-cyan-50 border-cyan-200' : 'bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20'}>
                  <CardContent className="p-6">
                    {isCommunityBoard ? (
                      <div className="text-center">
                        <div className="text-cyan-700 font-semibold mb-2">
                          {myApplication?.created_at
                            ? `Application sent ${new Date(myApplication.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                            : 'You have already applied for this job'}
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">
                            Didn't send the email? Click below to open it again.
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-cyan-300 text-cyan-700 hover:bg-cyan-100"
                            onClick={handleCBResend}
                            disabled={cbApplying}
                          >
                            <Send size={14} className="mr-2" />
                            {cbApplying ? 'Opening...' : 'Resend Application Email'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                    <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                      <span className="text-primary">✓</span>
                      {t('application.yourApplication')}
                    </h3>
                    <div className="space-y-3 mb-4">
                      {myApplication.cover && (
                        <div className="pt-2 border-t">
                          <p className="text-sm font-medium mb-1">{t('application.coverLetter')}:</p>
                          <p className="text-sm text-muted-foreground">{myApplication.cover}</p>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-2">
                        <span className="text-sm font-medium">{t('application.status')}:</span>
                        <Badge className={myApplication.status === 'accepted' || myApplication.status === 'revealed' || myApplication.status === 'shortlisted' ? 'bg-green-500' : ''}>
                          {t(`application.status_${myApplication.status}`)}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {/* Show Chat ONLY if accepted/revealed */}
                      {canChat && (
                        <Button
                          size="lg"
                          variant="default"
                          className="flex-1 rounded-xl bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => navigate(`/messages?user=${job.employer_id}`)}
                        >
                          <MessageCircle size={16} className="mr-2" />
                          {t('application.chatWithEmployer')}
                        </Button>
                      )}
                      {/* Show Withdraw ONLY if applied/pending */}
                      {canWithdraw && (
                        <Button
                          size="lg"
                          variant="destructive"
                          className="rounded-xl"
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const { data: session } = await supabase.auth.getSession();
                              if (!session?.session?.access_token) {
                                toast.error(t('auth.pleaseLogin'));
                                return;
                              }

                              const response = await fetch(
                                `${config.BACKEND_URL}/api/applications/${myApplication.id}/withdraw`,
                                {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${session.session.access_token}`
                                  }
                                }
                              );

                              if (!response.ok) {
                                // Clone response before reading body to prevent "body already used" error
                                const errorData = await response.clone().json();
                                throw new Error(errorData.detail || t('application.withdrawFailed'));
                              }
                              
                              toast.success(t('application.withdrawSuccess'));
                              setMyApplication({ ...myApplication, status: 'withdrawn' });
                              
                              // Force refetch to ensure UI consistency
                              setTimeout(() => {
                                checkMyApplication();
                              }, 100);
                            } catch (error) {
                              console.error('Withdraw error:', error);
                              toast.error(error.message || t('application.withdrawFailed'));
                            }
                          }}
                        >
                          <Trash size={16} className="mr-2" />
                          {t('application.withdraw')}
                        </Button>
                      )}
                    </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Employer SMS blast was removed entirely 2026-07-20: sending marketing SMS
                to job seekers is not permitted (DPA/NTC). Do not reintroduce. */}

            {/* Homepage banner enticement — job owner, job (not gig), no active banner yet.
                Bright-gold treatment (user request): saturated gradient + glow so it's
                impossible to miss, mirroring the "Featured" gold identity. */}
            {pricing.banner_show_job_details && isJobOwnerEmployer && !isGig && hasActiveBanner === false && (
              <Card className="p-4 mb-6 border-[1.5px] border-[#E08A0B] bg-gradient-to-br from-[#FFE067] to-[#F5A623] shadow-[0_6px_22px_-6px_rgba(240,160,20,0.6)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="font-extrabold text-sm text-[#4a3209] flex items-center gap-1.5">
                    <span aria-hidden="true">⭐</span>
                    {t('banner.applicantsPromptTitle')}
                  </p>
                  <p className="text-xs text-[#6b4a0e] font-medium mt-1">{t('banner.applicantsPromptBody')}</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => setShowBannerCreateModal(true)}
                  className="shrink-0 bg-gradient-to-br from-[#F5A623] to-[#E0820A] hover:from-[#E0820A] hover:to-[#c9740a] text-white font-bold border border-[#c9740a] shadow-md"
                  data-testid="job-details-banner-prompt-cta"
                >
                  {t('banner.applicantsPromptCta')}
                </Button>
              </Card>
            )}

            {canViewApplications && (
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-2xl font-semibold">
                    {isGig ? 'Active Bids' : 'Active Applications'} ({applications.length})
                    {isAdmin && !isEmployer && <span className="text-sm font-normal text-muted-foreground ml-2">(Admin View)</span>}
                  </h2>
                  {!isGig && applications.length > 0 && isEmployer && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportApplicants}
                      disabled={exporting}
                      className="flex items-center gap-2"
                    >
                      <Download size={16} />
                      {exporting ? t('common.exporting', 'Exporting...') : t('job.exportToExcel', 'Export to Excel')}
                    </Button>
                  )}
                </div>

                {loadingApplications ? (
                  <div className="flex justify-center py-8">
                    <div className="spinner" />
                  </div>
                ) : applications.length === 0 ? (
                  <Card className="p-8 text-center bg-muted/30">
                    <Briefcase className="mx-auto mb-3 text-muted-foreground" size={40} />
                    <p className="text-muted-foreground">
                      No {isGig ? 'bids' : 'applications'} yet for this {isGig ? 'gig' : 'job'}
                    </p>
                  </Card>
                ) : (
                  <>
                    <Card>
                      <div className="overflow-x-auto max-w-full">
                        <table className="w-full">
                          <thead className="border-b bg-muted/30">
                            <tr>
                              <th className="py-3 px-4 text-left font-semibold text-sm">
                                {isGig ? 'Bidder' : 'Applicant'}
                              </th>
                              {isGig && (
                                <th className="py-3 px-4 text-left font-semibold text-sm hidden lg:table-cell">Bid Amount</th>
                              )}
                              <th className="py-3 px-4 text-left font-semibold text-sm hidden md:table-cell">Skills</th>
                              <th className="py-3 px-4 text-left font-semibold text-sm hidden sm:table-cell">AI Match</th>
                              <th className="py-3 px-4 text-left font-semibold text-sm hidden sm:table-cell">
                                {isGig ? 'Bid Date' : 'Applied'}
                              </th>
                              <th className="py-3 px-4 text-right font-semibold text-sm">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {applications.slice(0, visibleCount).map((application) => {
                              // For bids use contractor, for applications use seeker
                              const applicant = application.contractor || application.seeker;
                              const isNew = !application.viewed_at;

                              return (
                                <tr
                                  key={application.id}
                                  className={`hover:bg-muted/50 transition-colors cursor-pointer ${isNew ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                                  onClick={() => {
                                    setSelectedApplicant(application);
                                    setShowApplicantDetail(true);
                                    if (isNew) markAsViewed(application.id);
                                  }}
                                >
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-3">
                                      {isNew && (
                                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse flex-shrink-0" title="New - Not viewed yet" />
                                      )}
                                      <Avatar className="w-10 h-10">
                                        <AvatarFallback className="bg-primary/10 text-primary">
                                          {applicant?.name?.[0] || 'U'}
                                        </AvatarFallback>
                                      </Avatar>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          {applicant?.verified && (
                                            <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />
                                          )}
                                          <span className="font-medium text-sm">
                                            {applicant?.name || `Worker from ${applicant?.town || 'Bohol'}`}
                                          </span>
                                          {applicant?.peso_office && (
                                            <PesoBadge officeName={applicant.peso_office_name} color={applicant.peso_office_color} className="flex-shrink-0" />
                                          )}
                                          {isNew && (
                                            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">NEW</Badge>
                                          )}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          {applicant?.town}
                                          {applicant?.rating > 0 && (
                                            <span className="ml-2 inline-flex items-center gap-1 text-yellow-600">
                                              <Star size={10} className="fill-yellow-500" />
                                              {applicant.rating.toFixed(1)}
                                            </span>
                                          )}
                                        </div>
                                        {!isGig && DECIDABLE_STATUSES.includes(application.status) && !['filled', 'closed'].includes(job?.status) && (
                                          <div className="flex flex-wrap gap-1.5 mt-1.5" onClick={(e) => e.stopPropagation()}>
                                            <button
                                              type="button"
                                              disabled={!!decidingId}
                                              aria-pressed={application.status === 'shortlisted'}
                                              onClick={() => { if (application.status !== 'shortlisted') setApplicationDecision(application, 'moving_forward'); }}
                                              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap disabled:opacity-50 ${application.status === 'shortlisted' ? 'bg-green-600 border-green-600 text-white' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
                                              data-testid="decision-moving-forward"
                                            >
                                              <ThumbsUp size={11} />
                                              {t('dashboard.movingForward', 'Moving forward')}
                                            </button>
                                            <button
                                              type="button"
                                              disabled={!!decidingId}
                                              aria-pressed={application.status === 'rejected'}
                                              onClick={() => { if (application.status !== 'rejected') setApplicationDecision(application, 'not_a_fit'); }}
                                              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap disabled:opacity-50 ${application.status === 'rejected' ? 'bg-gray-600 border-gray-600 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                              data-testid="decision-not-a-fit"
                                            >
                                              <XCircle size={11} />
                                              {t('dashboard.notAFit', 'Not a fit this time')}
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  {isGig && (
                                    <td className="py-3 px-4 hidden lg:table-cell">
                                      <div className="font-semibold text-primary">
                                        ₱{application.amount?.toLocaleString() || '0'}
                                      </div>
                                      {application.timeline && (
                                        <div className="text-xs text-muted-foreground">
                                          {application.timeline}
                                        </div>
                                      )}
                                    </td>
                                  )}
                                  <td className="py-3 px-4 hidden md:table-cell">
                                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                                      {applicant?.skills?.slice(0, 3).map((skill, idx) => (
                                        <Badge key={idx} variant="secondary" className="text-xs truncate">
                                          {skill}
                                        </Badge>
                                      )) || <span className="text-xs text-muted-foreground">No skills listed</span>}
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 hidden sm:table-cell">
                                    {application.match_score != null ? (
                                      <Badge className={`${getMatchColor(application.match_score)} text-white text-xs`}>
                                        {application.match_score}%
                                      </Badge>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">--</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4 hidden sm:table-cell">
                                    <div className="text-xs text-muted-foreground">
                                      {new Date(application.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </div>
                                  </td>
                                  <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex gap-2 justify-end">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="rounded-xl text-xs"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedApplicant(application);
                                          setShowApplicantDetail(true);
                                          // opening the profile counts as seeing the applicant (no "waiting" nudge)
                                          if (!application.viewed_at) markAsViewed(application.id);
                                        }}
                                        data-testid="view-profile-button"
                                      >
                                        View Profile
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="rounded-xl text-xs"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!application.viewed_at) markAsViewed(application.id);
                                          navigate(`/messages?user=${applicant?.id}`);
                                        }}
                                        data-testid="chat-button"
                                      >
                                        <MessageCircle size={14} className="mr-1" />
                                        Chat
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                    
                    {applications.length > visibleCount && (
                      <div className="mt-4 flex justify-center">
                        <Button
                          variant="outline"
                          onClick={() => setVisibleCount(prev => prev + 10)}
                          className="rounded-xl"
                          data-testid="load-more-applications"
                        >
                          Load More
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Apply Dialog */}
      {/* Apply Dialog for Jobs */}
      <Dialog open={showApplyDialog} onOpenChange={(open) => { if (!open) closeApplyDialog(); else setShowApplyDialog(true); }}>
        <DialogContent data-testid="apply-dialog" className="max-h-[90vh] flex flex-col">
          {applyStep === 'next' ? (
          <NextJobsPanel
            t={t}
            jobs={nextJobs}
            loading={nextJobsLoading}
            defaultCover={cover}
            appliedIds={nextApplied}
            onApply={applyToSuggestedJob}
            onDone={closeApplyDialog}
          />
          ) : (
          <>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{t('jobDetails.applyFor', 'Apply for')} {getLocalizedField(job, 'title')}</DialogTitle>
            <DialogDescription className="text-left space-y-2">
              <span className="block">
                <strong>{t('jobDetails.whatIsCoverLetter', 'What is a Cover Letter?')}</strong> {t('jobDetails.coverLetterExplainer', 'A cover letter is a short message introducing yourself to the employer. It helps them understand why you\'re interested in this job and what makes you a good fit.')}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-4 overflow-y-auto flex-1">
            {/* Profile completion reminder */}
            {profile && (!profile.work_experience || profile.work_experience.length === 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm">
                <p className="text-amber-800">
                  <strong>{t('jobDetails.tip', 'Tip')}:</strong> {t('jobDetails.completeProfileTip', 'Complete your profile with your work experience before applying. Employers prefer candidates with detailed profiles - it increases your chances of getting hired!')}
                </p>
                <Button
                  variant="link"
                  className="text-amber-700 p-0 h-auto font-semibold mt-1 whitespace-normal text-left"
                  onClick={goToProfileKeepingApplication}
                  data-testid="apply-go-to-profile"
                >
                  {t('jobDetails.goToProfileKeep', 'Add work experience — your application is saved')}
                </Button>
              </div>
            )}

            {/* Phone number, asked right here when the profile has none (no detour) */}
            {needsPhone && (
              <div className="space-y-2">
                <Label htmlFor="apply-phone" className="text-sm font-semibold">
                  {t('jobDetails.phoneForEmployer', 'Your mobile number')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="apply-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="0917 123 4567"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value.slice(0, 20))}
                  className="rounded-xl"
                  data-testid="apply-phone-input"
                />
                <p className="text-xs text-gray-500">
                  {t('jobDetails.phoneForEmployerHint', 'So the employer can call you for an interview. It is saved to your profile.')}
                </p>
              </div>
            )}

            {/* Custom Screening Question */}
            {job?.custom_question && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-primary">
                  {t('jobDetails.employerQuestion', "Employer's Question")} <span className="text-red-500">*</span>
                </Label>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                  <p className="text-sm text-blue-800">{job.custom_question}</p>
                </div>
                <Textarea
                  placeholder={t('jobDetails.typeAnswerHere', 'Type your answer here...')}
                  value={customAnswer}
                  onChange={(e) => setCustomAnswer(e.target.value)}
                  rows={3}
                  className="rounded-xl"
                  maxLength={2000}
                  data-testid="custom-answer-input"
                />
                <p className="text-xs text-gray-500 text-right">
                  {customAnswer.length}/2000 {t('common.characters', 'characters')}
                </p>
              </div>
            )}

            {/* Required Documents */}
            {job?.required_documents && job.required_documents.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <FileText size={16} />
                  {t('jobDetails.requiredDocuments', 'Required Documents')}
                </Label>
                <p className="text-xs text-gray-500">
                  {t('jobDetails.uploadDocsHint', 'Upload the documents requested by the employer. Accepted: Images, PDFs, and Word documents (max 10MB each)')}
                </p>
                {jobDocs.ifHired.length > 0 && (
                  <p className="text-xs text-emerald-700" data-testid="if-hired-docs-hint">
                    {t('jobDocs.applyHint', 'Documents marked "If hired" can wait — you can apply now and bring them later.')}
                  </p>
                )}
                <div className="space-y-2">
                  {job.required_documents.map((doc, index) => {
                    const isUploaded = uploadedDocuments.some(d => d.name === doc.name);
                    const isUploading = uploadingDoc === doc.name;
                    const isRequired = doc.required !== false;
                    const bringingLater = !isRequired && !isUploaded && laterDocs.includes(doc.name);

                    return (
                      <div key={index} className={`flex items-center gap-3 p-3 rounded-xl border ${isUploaded ? 'bg-green-50 border-green-200' : bringingLater ? 'bg-emerald-50/60 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium flex flex-wrap items-center gap-2">
                            <span className="break-words min-w-0">{doc.name}</span>
                            {isRequired && <span className="text-red-500 text-xs">*</span>}
                            {!isRequired && (
                              <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                                {t('jobDocs.ifHired', 'If hired')}
                              </span>
                            )}
                            {isUploaded && <CheckCircle size={14} className="text-green-600" />}
                          </p>
                          {!isRequired && !isUploaded && (
                            <label className="mt-1.5 flex items-center gap-2 text-xs text-gray-700 cursor-pointer min-h-[32px]">
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-emerald-600"
                                checked={bringingLater}
                                onChange={(e) => setLaterDocs((prev) => (e.target.checked
                                  ? [...prev.filter((n) => n !== doc.name), doc.name]
                                  : prev.filter((n) => n !== doc.name)))}
                                data-testid="bring-later-checkbox"
                              />
                              {t('jobDocs.bringLater', "I'll bring this later")}
                            </label>
                          )}
                          {isUploaded && (
                            <p className="text-xs text-green-600 mt-0.5 break-words">
                              ✓ {uploadedDocuments.find(d => d.name === doc.name)?.filename}
                            </p>
                          )}
                          {savedDocs.length > 0 && !bringingLater && (
                            <select
                              className="mt-1.5 w-full max-w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs"
                              value=""
                              onChange={(e) => { if (e.target.value) pickSavedDocument(doc.name, e.target.value); }}
                              aria-label={t('jobDetails.useSavedFile', 'Use a saved file')}
                              data-testid="use-saved-file"
                            >
                              <option value="">{t('jobDetails.useSavedFile', 'Use a saved file')}…</option>
                              {savedDocs.map((sd) => (
                                <option key={sd.path} value={sd.path}>{sd.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        {!bringingLater && (
                        <label className={`cursor-pointer shrink-0 ${isUploading ? 'opacity-50' : ''}`}>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*,.pdf,.doc,.docx"
                            disabled={isUploading}
                            onChange={(e) => handleDocumentUpload(e, doc.name)}
                          />
                          <div className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${isUploaded ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-primary text-white hover:bg-primary/90'}`}>
                            {isUploading ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload size={14} />
                                {isUploaded ? 'Replace' : 'Upload'}
                              </>
                            )}
                          </div>
                        </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Cover Letter */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">{t('jobDetails.coverLetterOptional', 'Cover Letter (Optional)')}</Label>
              <Textarea
                placeholder={t('jobDetails.coverLetterPlaceholder', "Example: 'Hi, I'm interested in this position because I have 2 years of experience in similar work. I'm reliable, hardworking, and can start immediately. I look forward to hearing from you.'")}
                value={cover}
                onChange={(e) => setCover(e.target.value)}
                rows={5}
                className="rounded-xl"
                data-testid="cover-letter-input"
              />
              <p className="text-xs text-gray-500">
                {t('jobDetails.coverLetterHint', 'Write a few sentences about yourself, your experience, and why you want this job.')}
              </p>
            </div>
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => setShowApplyDialog(false)}
              disabled={applying}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleApply}
              disabled={applying}
              data-testid="submit-application-btn"
            >
              {applying ? t('jobDetails.submitting', 'Submitting...') : t('jobDetails.submitApplication', 'Submit Application')}
            </Button>
          </DialogFooter>
          </>
          )}
        </DialogContent>
      </Dialog>

      {/* Community Board Apply Dialog */}
      <Dialog open={showCBApplyDialog} onOpenChange={setShowCBApplyDialog}>
        <DialogContent className="max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Apply for {getLocalizedField(job, 'title')}</DialogTitle>
            <DialogDescription className="text-left">
              Your application will be logged, then your email app will open with a pre-filled message to the employer. Remember to attach your resume!
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-4 overflow-y-auto flex-1">
            {/* Profile summary (read-only) */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <p className="text-sm font-medium">Your Info (included in email)</p>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Name: <span className="text-foreground">{profile?.name || '-'}</span></p>
                <p>Phone: <span className="text-foreground">{profile?.phone || '-'}</span></p>
              </div>
            </div>

            {/* Cover letter */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Message to Employer (Optional)</Label>
              <Textarea
                placeholder="Hi, I'm interested in this position because..."
                value={cbCover}
                onChange={(e) => setCbCover(e.target.value)}
                rows={4}
                className="rounded-xl"
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground">
                This message will be included in the email body.
              </p>
            </div>

            {/* Resume reminder */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-sm text-amber-800">
                <strong>Reminder:</strong> After clicking submit, your email app will open. Make sure to attach your resume before sending!
              </p>
            </div>
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => setShowCBApplyDialog(false)}
              disabled={cbApplying}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCBApply}
              disabled={cbApplying}
            >
              {cbApplying ? 'Submitting...' : 'Submit & Open Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Claim Dialog */}
      <Dialog open={showClaimDialog} onOpenChange={(open) => {
        if (!open) {
          setShowClaimDialog(false);
          setClaimStep('request');
          setClaimCode('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {claimStep === 'request' ? 'Claim This Listing' : 'Enter Verification Code'}
            </DialogTitle>
            <DialogDescription className="text-left">
              {claimStep === 'request' ? (
                'We\'ll send a 6-digit verification code to the email address associated with this job post. You must have access to that email to claim this listing.'
              ) : (
                <>Enter the 6-digit code sent to <strong>{claimEmail}</strong>. The code expires in 15 minutes.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            {claimStep === 'request' ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800">
                <p className="font-medium mb-1">What happens when you claim?</p>
                <ul className="list-disc list-inside space-y-1 text-emerald-700">
                  <li>This listing moves to your employer account</li>
                  <li>Seekers can apply directly through the platform</li>
                  <li>You can manage applications from your dashboard</li>
                </ul>
              </div>
            ) : (
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Verification Code</Label>
                <Input
                  placeholder="Enter 6-digit code"
                  value={claimCode}
                  onChange={(e) => setClaimCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="rounded-xl text-center text-2xl tracking-[0.3em] font-mono"
                  maxLength={6}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground text-center">
                  Didn't receive the code? Check spam or{' '}
                  <button
                    type="button"
                    className="text-primary underline hover:no-underline"
                    onClick={handleClaimRequest}
                    disabled={claimLoading}
                  >
                    resend
                  </button>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowClaimDialog(false);
                setClaimStep('request');
                setClaimCode('');
              }}
              disabled={claimLoading}
            >
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={claimStep === 'request' ? handleClaimRequest : handleClaimVerify}
              disabled={claimLoading || (claimStep === 'verify' && claimCode.length !== 6)}
            >
              {claimLoading ? (
                <><Loader2 size={16} className="mr-2 animate-spin" /> {claimStep === 'request' ? 'Sending...' : 'Verifying...'}</>
              ) : (
                claimStep === 'request' ? 'Send Verification Code' : 'Verify & Claim'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bid Dialog for Gigs */}
      <Dialog open={showBidDialog} onOpenChange={setShowBidDialog}>
        <DialogContent data-testid="bid-dialog">
          <DialogHeader>
            <DialogTitle>{t('jobDetails.submitBidFor', 'Submit Bid for')} {job.title}</DialogTitle>
            <DialogDescription>
              {t('jobDetails.provideBidDetails', 'Provide your bid details for this gig')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="bid-amount">{t('jobDetails.bidAmount', 'Bid Amount (₱)')} *</Label>
              <Input
                id="bid-amount"
                type="number"
                placeholder={t('jobDetails.enterBidAmount', 'Enter your bid amount')}
                value={bidData.amount}
                onChange={(e) => setBidData({ ...bidData, amount: e.target.value })}
                onWheel={(e) => e.target.blur()}
                className="rounded-xl"
                data-testid="bid-amount-input"
              />
            </div>
            <div>
              <Label htmlFor="bid-timeline">{t('jobDetails.timeline', 'Timeline')} *</Label>
              <Input
                id="bid-timeline"
                placeholder={t('jobDetails.timelinePlaceholder', 'e.g., 2 weeks, 1 month')}
                value={bidData.timeline}
                onChange={(e) => setBidData({ ...bidData, timeline: e.target.value })}
                className="rounded-xl"
                data-testid="bid-timeline-input"
              />
            </div>
            <div>
              <Label htmlFor="bid-description">{t('jobDetails.proposal', 'Proposal')}</Label>
              <Textarea
                id="bid-description"
                placeholder={t('jobDetails.proposalPlaceholder', "Describe your approach and why you're the best fit...")}
                value={bidData.description}
                onChange={(e) => setBidData({ ...bidData, description: e.target.value })}
                rows={4}
                className="rounded-xl"
                data-testid="bid-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBidDialog(false)}
              disabled={bidding}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleBid}
              disabled={bidding}
              data-testid="submit-bid-btn-dialog"
            >
              {bidding ? t('jobDetails.submitting', 'Submitting...') : t('jobDetails.submitBid', 'Submit Bid')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reviews List Modal */}
      <ReviewsListModal
        open={showReviewsModal}
        onOpenChange={setShowReviewsModal}
        userId={job.employer_id}
        userName={job.employer?.business_name || job.employer?.name}
      />

      {/* Applicant Detail Modal */}
      <ApplicantDetailModal
        open={showApplicantDetail}
        onOpenChange={setShowApplicantDetail}
        applicant={selectedApplicant}
        job={job}
        revealed={true}
      />

      {/* Edit Job Modal */}
      <EditJobModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        job={job}
        onSuccess={handleEditSuccess}
      />

      {/* Homepage Banner Create Modal (enticement CTA above Active Applications) */}
      {showBannerCreateModal && job && (
        <BannerCreateModal
          jobId={job.id}
          onClose={() => {
            setShowBannerCreateModal(false);
            checkMyBanners(job.id); // re-check so the prompt only hides if a banner was actually created
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('job.deleteTitle', 'Delete Job Post?')}</DialogTitle>
            <DialogDescription>
              {t('job.deleteConfirmation', 'Are you sure you want to delete this job post? This action cannot be undone. All applications for this job will also be removed.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? t('job.deleting', 'Deleting...') : t('job.deleteButton', 'Delete Job')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      {job?.image_url && (
        <ImageLightbox
          src={fixImageUrl(job.image_url)}
          alt={job.title}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      <BottomNav />
    </div>
  );
};

export default JobDetails;
